import React, { useState, useContext } from 'react';
import { Box, Button, Typography, Paper, styled } from '@mui/material';
import { OrgContext } from '../contexts/OrgContext';
import { useNavigate } from 'react-router-dom';
import { bulkCreateSuppliers } from '../services/supplierService';
import Papa from 'papaparse';
import TanStackTable from '../components/TanStackTable';
import BackButton from '../components/BackButton';

const HiddenInput = styled('input')({
  display: 'none',
});

const SuppliersBulkImport = () => {
  const { orgId } = useContext(OrgContext);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleDownloadTemplate = () => {
    const csvContent = 'data:text/csv;charset=utf-8,'
      + 'supplier_name,email,contact_number,address,lead_time\n'
      + 'ABC Supplies,contact@abcsupplies.com,+1234567890,"123 Industrial Park, Business City, USA",7\n'
      + 'XYZ Manufacturing,info@xyzmanufacturing.com,+0987654321,"456 Factory Lane, Industry Town, USA",14\n'
      + 'Global Distributors,orders@globaldist.com,+1122334455,"789 Logistics Ave, Port City, USA",21\n';
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'suppliers_template.csv');
    document.body.appendChild(link);
    link.click();
  };

  const handleUpload = () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    setError(null);

    Papa.parse(file, {
      header: true,
      complete: async (parseResults) => {
        const suppliers = parseResults.data.map((supplier, index) => ({
          supplier_name: supplier.supplier_name,
          email: supplier.email,
          contact_number: supplier.contact_number,
          address: supplier.address,
          lead_time: supplier.lead_time ? parseInt(supplier.lead_time, 10) : undefined,
          org_id: parseInt(orgId, 10),
          row: index + 2, // Add row number for error tracking (1 for header, so start from 2)
        }));

        try {
          const response = await bulkCreateSuppliers(suppliers);
          setResults(response);
        } catch (err) {
          setError('Error uploading suppliers.');
          console.error(err);
        }
      },
      error: (err, file, inputElem, reason) => {
        setError(`Error parsing CSV file at row ${err.row}: ${reason}`);
        console.error(err);
      },
    });
  };

  const handleDone = () => {
    navigate(`/${orgId}/suppliers`);
  };

  return (
    <Paper sx={{ p: 2, backgroundColor: '#1e1e1e', color: '#ffffff' }}>
      <BackButton />
      <Typography variant="h5" gutterBottom>
        Bulk Import Suppliers
      </Typography>
      <Typography variant="body1" gutterBottom>
        Follow the instructions below to bulk import suppliers:
      </Typography>
      <Typography variant="body2" gutterBottom>
        1. Download the template CSV file.
      </Typography>
      <Typography variant="body2" gutterBottom>
        2. Fill in the supplier details in the CSV file. Ensure no extra rows are present, as they will lead to failures during the upload.
      </Typography>
      <Typography variant="body2" gutterBottom>
        3. Required fields: supplier_name, contact_number.
      </Typography>
      <Typography variant="body2" gutterBottom>
        4. Optional fields: email, address, lead_time.
      </Typography>
      <Typography variant="body2" gutterBottom>
        5. Upload the completed CSV file and start bulk import.
      </Typography>

      <Button variant="contained" onClick={handleDownloadTemplate} sx={{ my: 2, mr: 2 }}>
        Download Template CSV
      </Button>

      <label htmlFor="contained-button-file">
        <HiddenInput
          accept=".csv"
          id="contained-button-file"
          type="file"
          onChange={handleFileChange}
        />
        <Button variant="contained" component="span">
          Select CSV File
        </Button>
      </label>

      {file && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          Selected File: {file.name}
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={handleUpload}>
          Start Bulk Import
        </Button>
      </Box>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      {results && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Bulk Import Results
          </Typography>
          <Typography sx={{ mb: 3, color: "#ddd" }} variant="body2" gutterBottom>
            Scroll down to see the successful and failed imports.
          </Typography>
          <Typography variant="body2" gutterBottom>
            Successful Imports
          </Typography>
          <Box sx={{ mb: 2 }}>
            <TanStackTable
              data={results.successes || []}
              columns={[
                { header: 'ID', accessorKey: 'id' },
                { header: 'Supplier Name', accessorKey: 'supplier_name' },
                { header: 'Email', accessorKey: 'email' },
                { header: 'Contact Number', accessorKey: 'contact_number' },
                { header: 'Lead Time', accessorKey: 'lead_time' },
              ]}
            />
          </Box>
          <Typography variant="body2" gutterBottom>
            Failed Imports
          </Typography>
          <Box>
            <TanStackTable
              data={(results.failures || []).map((f) => ({
                ...f.dto,
                error: f.error,
                row: f.dto.row,
              }))}
              columns={[
                { header: 'Row', accessorKey: 'row' },
                { header: 'Supplier Name', accessorKey: 'supplier_name' },
                { header: 'Email', accessorKey: 'email' },
                { header: 'Contact Number', accessorKey: 'contact_number' },
                { header: 'Error', accessorKey: 'error' },
              ]}
            />
          </Box>
          <Button variant="contained" onClick={handleDone} sx={{ mt: 2 }}>
            Done
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default SuppliersBulkImport;
